import { getEnvRequired } from '../../shared/utilities/env';

// === TYPES ===

export type DayKey =
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday';

export type ContactType = 'audio_request_manager' | 'visio_request_manager' | 'other';

type MarketCode = 'FR' | 'ES' | 'IT' | 'DE' | 'NL';

interface TimeOption {
    value: string;
    label: string;
}

export interface Period {
    startTime: string;
    endTime: string;
}

// UI state variant — allows undefined while a new slot is being filled in
export interface UIPeriod {
    startTime: string | undefined;
    endTime: string | undefined;
}

// Internal UI state — includes `active` toggle not present in API contract
export interface InternalDayState {
    active: boolean;
    periods: UIPeriod[];
}

type SlotError = 'undefined_start' | 'undefined_end' | 'start_after_end' | 'overlap_next';

type AgendaDays = Record<DayKey, Period[]>;

// === TIME UTILITIES ===

/** "HH:MM" string → total minutes (e.g. "09:30" → 570). */
export function timeToMinutes(timeStr: string): number {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
}

/** Total minutes → zero-padded "HH:MM" string (e.g. 570 → "09:30"). */
export function minutesToTime(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Returns array of {value, label} for every 15-min step from minMinutes to maxMinutes inclusive */
export function generateTimeOptions(minMinutes: number, maxMinutes: number): TimeOption[] {
    const options: TimeOption[] = [];
    for (let t = minMinutes; t <= maxMinutes; t += 15) {
        const val = minutesToTime(t);
        options.push({ value: val, label: val });
    }
    return options;
}

/** Max allowed start = end - duration */
export function getStartFieldMax(endTime: string, duration: number): string {
    return minutesToTime(timeToMinutes(endTime) - duration);
}

/** Min allowed end = start + duration */
export function getEndFieldMin(startTime: string, duration: number): string {
    return minutesToTime(timeToMinutes(startTime) + duration);
}

function validateSlot(periods: UIPeriod[], index: number): SlotError[] {
    const { startTime, endTime } = periods[index];
    const errors: SlotError[] = [];

    if (startTime === undefined) errors.push('undefined_start');
    if (endTime === undefined) errors.push('undefined_end');
    if (startTime === undefined || endTime === undefined) return errors;

    const startMin = timeToMinutes(startTime);
    const endMin = timeToMinutes(endTime);

    if (startMin >= endMin) {
        errors.push('start_after_end');
        return errors;
    }

    const next = periods[index + 1];
    if (next?.startTime !== undefined && endMin >= timeToMinutes(next.startTime)) {
        errors.push('overlap_next');
    }

    return errors;
}

/** Returns per-slot validation errors. Key = slot index, value = list of errors. */
export function validatePeriods(periods: UIPeriod[]): Record<number, SlotError[]> {
    const errors: Record<number, SlotError[]> = {};
    for (let i = 0; i < periods.length; i++) {
        const slotErrors = validateSlot(periods, i);
        if (slotErrors.length > 0) errors[i] = slotErrors;
    }
    return errors;
}

/** Min start for 2nd slot = end of 1st slot + 15 min */
export function getSecondSlotStartMin(prevEndStr: string): string {
    return minutesToTime(timeToMinutes(prevEndStr) + 15);
}

/**
 * Default slot when agent clicks "+ Ajouter créneau":
 * start = prevEnd + 15 min (capped at 23:30), end = start + 2h (capped at 23:45).
 */
export function getDefaultSecondSlot(prevEndStr: string): Period {
    const MAX = 23 * 60 + 45;
    const start = Math.min(timeToMinutes(prevEndStr) + 15, MAX - 15);
    const end = Math.min(start + 120, MAX);
    return { startTime: minutesToTime(start), endTime: minutesToTime(end) };
}

// === DAY CONSTANTS ===

export const DAY_KEYS: DayKey[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
];

const DAY_LABELS: Record<DayKey, string> = {
    monday: 'Lun',
    tuesday: 'Mar',
    wednesday: 'Mer',
    thursday: 'Jeu',
    friday: 'Ven',
    saturday: 'Sam',
    sunday: 'Dim',
};

export const DAY_LABELS_LONG: Record<DayKey, string> = {
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche',
};

/** Maps API DayKey to the French abbreviation used in data-spec-id attributes. */
export const DAY_SPEC_IDS: Record<DayKey, string> = {
    monday: 'lun',
    tuesday: 'mar',
    wednesday: 'mer',
    thursday: 'jeu',
    friday: 'ven',
    saturday: 'sam',
    sunday: 'dim',
};

// === SIDEBAR SUMMARY ===

/** Builds the sidebar days label: arrow range for contiguous days, comma list otherwise. */
export function getSidebarJoursText(activeDays: Partial<Record<DayKey, boolean>>): string {
    const active = DAY_KEYS.filter((k) => activeDays[k]);
    if (active.length === 0) return '—';
    if (active.length === 1) return DAY_LABELS[active[0]];
    const firstIdx = DAY_KEYS.indexOf(active[0]);
    const lastIdx = DAY_KEYS.indexOf(active[active.length - 1]);
    const contiguous = active.length === lastIdx - firstIdx + 1;
    if (contiguous) {
        return `${DAY_LABELS[active[0]]} → ${DAY_LABELS[active[active.length - 1]]}`;
    }
    return active.map((k) => DAY_LABELS[k]).join(', ');
}

/** Maps a contact type to its sidebar display label. */
export function getSidebarContactText(contactType: ContactType): string {
    if (contactType === 'audio_request_manager') return 'Téléphone via RM';
    if (contactType === 'visio_request_manager') return 'Visio via RM';
    return 'Autre';
}

// === PHONE NUMBERS ===

const PHONE_NUMBERS: Record<MarketCode, string> = {
    FR: '+33 1 75 85 02 57',
    ES: '+33 1 75 85 03 52',
    IT: '+33 1 86 47 27 02',
    DE: '+33 1 86 47 27 01',
    NL: '+31 97 01 02 59 66 2',
};

/** Returns the Evaneos booking phone number for a market code, or null if unknown. */
export function getPhoneNumberForMarket(marketCode: string): string | null {
    return PHONE_NUMBERS[marketCode as MarketCode] ?? null;
}

// === TIMEZONE CONVERSION ===

/** Returns offset in minutes of timezone vs UTC (positive = ahead of UTC). */
export function getTimezoneOffsetMinutes(timezone: string): number {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const tzH = parseInt(parts.find((p) => p.type === 'hour')!.value);
    const tzM = parseInt(parts.find((p) => p.type === 'minute')!.value);
    const utcH = now.getUTCHours();
    const utcM = now.getUTCMinutes();
    let offset = tzH * 60 + tzM - (utcH * 60 + utcM);
    if (offset > 12 * 60) offset -= 24 * 60;
    if (offset < -12 * 60) offset += 24 * 60;
    return offset;
}

/** Convert "HH:MM" local time in timezone → "HH:MM" UTC. */
export function localToUTC(timeStr: string, timezone: string): string {
    const localMin = timeToMinutes(timeStr);
    const offset = getTimezoneOffsetMinutes(timezone);
    return minutesToTime((((localMin - offset) % (24 * 60)) + 24 * 60) % (24 * 60));
}

/** Convert "HH:MM" UTC → "HH:MM" local time in timezone. */
export function utcToLocal(timeStr: string, timezone: string): string {
    const utcMin = timeToMinutes(timeStr);
    const offset = getTimezoneOffsetMinutes(timezone);
    return minutesToTime((((utcMin + offset) % (24 * 60)) + 24 * 60) % (24 * 60));
}

// === CONFIG VALIDITY ===

/** Config is valid if at least 1 day has at least 1 period */
export function isConfigValid(days: AgendaDays): boolean {
    return Object.values(days).some((day) => day.length > 0);
}

// === GOOGLE OAUTH ===

const GOOGLE_CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar';

// Clé sessionStorage où le state CSRF est persisté entre la redirection vers Google
// et le retour sur le callback. sessionStorage est isolé par onglet, donc deux flows
// parallèles dans des onglets différents ne se marchent pas dessus.
export const OAUTH_STATE_KEY = 'google_oauth_state';

function generateOAuthState(): string {
    // 32 octets aléatoires cryptographiquement sûrs → 64 caractères hex.
    // Math.random() est exclu : il est prévisible et ne convient pas à un usage CSRF.
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Construit l'URL d'autorisation Google en y incluant un paramètre `state` anti-CSRF
// (RFC 6749 §10.12). Le state est une valeur aléatoire imprévisible générée ici,
// stockée en sessionStorage, puis vérifiée à l'identique sur le callback.
// Sans ce mécanisme, un attaquant pourrait forger un callback avec un code volé et
// le faire traiter par l'app au nom d'une victime connectée (account injection).
export function buildGoogleOAuthUrl(): string {
    const basePath = getEnvRequired('RM_APP_BASENAME');
    const clientId = getEnvRequired('GOOGLE_OAUTH_CLIENT_ID');
    const callbackUrl = `${window.location.origin}${basePath}/calendar/callback`;
    const state = generateOAuthState();
    // Stocké en sessionStorage : Google nous le renvoie tel quel sur le callback,
    // on peut alors comparer les deux valeurs pour valider l'origine de la requête.
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
    const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: callbackUrl,
        response_type: 'code',
        scope: GOOGLE_CALENDAR_SCOPE,
        access_type: 'offline',
        prompt: 'consent',
        state,
    });
    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}
